// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Alien Manager - Central API for all alien interactions
 * 
 * This is the single source of truth for alien behavior, encounters, and interactions.
 * All components should use this API instead of directly accessing alien configs.
 * 
 * Hostility Levels:
 * - PEACEFUL (0): Never attacks, always friendly
 * - CAUTIOUS (1-3): Wary but not hostile, may flee
 * - NEUTRAL (4-6): Unpredictable, depends on circumstances
 * - AGGRESSIVE (7-9): Likely to attack, can be negotiated with
 * - HOSTILE (10): Always attacks, no negotiation
 */

import { getAlienSpecies, getAllAlienSpecies } from '../../config/AlienSpeciesConfig';
import * as ReputationManager from './ReputationManager';

// ============================================================================
// HOSTILITY SYSTEM
// ============================================================================

/**
 * Calculate alien hostility level (0-10)
 * @param {Object} species - Alien species object
 * @param {Object} context - Optional context with reputation
 * @returns {number} Hostility level 0-10
 */
export const getHostilityLevel = (species, context = {}) => {
  if (!species) return 5; // Default neutral

  // Map aggression trait to hostility level
  const aggressionMap = {
    'None': 0,
    'Very Low': 1,
    'Low': 3,
    'Medium': 5,
    'High': 7,
    'Very High': 9
  };

  let hostility = aggressionMap[species.traits.aggression] || 5;

  // Adjust based on temperament
  const temperamentModifiers = {
    'Peaceful': -2,
    'Friendly': -2,
    'Logical': -1,
    'Contemplative': -1,
    'Neutral': 0,
    'Mysterious': 0,
    'Cautious': 1,
    'Proud': 1,
    'Greedy': 1,
    'Aggressive': 2,
    'Hostile': 3
  };

  hostility += temperamentModifiers[species.traits.temperament] || 0;

  // Hostile flag overrides to maximum
  if (species.hostile) {
    hostility = 10;
  }

  // REPUTATION MODIFIER: Adjust hostility based on player reputation
  if (context.useReputation !== false) {
    const reputation = ReputationManager.getReputation(species.id);
    
    // Reputation affects hostility significantly
    // Allied (50+): -3 hostility
    // Friendly (10-49): -2 hostility
    // Neutral (-9 to 9): no change
    // Unfriendly (-10 to -49): +2 hostility
    // Enemy (-50 to -100): +3 hostility
    
    if (reputation >= 50) {
      hostility -= 3; // Allied
    } else if (reputation >= 10) {
      hostility -= 2; // Friendly
    } else if (reputation <= -50) {
      hostility += 3; // Enemy
    } else if (reputation <= -10) {
      hostility += 2; // Unfriendly
    }
  }

  // Clamp to 0-10 range
  return Math.max(0, Math.min(10, hostility));
};

/**
 * Get hostility category from level
 * @param {number} level - Hostility level 0-10
 * @returns {string} Category name
 */
export const getHostilityCategory = (level) => {
  if (level === 0) return 'PEACEFUL';
  if (level <= 3) return 'CAUTIOUS';
  if (level <= 6) return 'NEUTRAL';
  if (level <= 9) return 'AGGRESSIVE';
  return 'HOSTILE';
};

/**
 * Get color for hostility level
 * @param {number} level - Hostility level 0-10
 * @returns {string} Color hex code
 */
export const getHostilityColor = (level) => {
  if (level === 0) return '#00ff88'; // Green - peaceful
  if (level <= 3) return '#00ffff'; // Cyan - cautious
  if (level <= 6) return '#ffaa00'; // Orange - neutral
  if (level <= 9) return '#ff8800'; // Dark orange - aggressive
  return '#ff4444'; // Red - hostile
};

/**
 * Determine if alien will attack on sight
 * @param {Object} species - Alien species object
 * @param {Object} context - Encounter context (location, player reputation, etc.)
 * @returns {boolean} True if alien attacks immediately
 */
export const willAttackOnSight = (species, context = {}) => {
  const hostility = getHostilityLevel(species, context);
  
  // Check reputation directly - ENEMY status means always hostile
  if (context.useReputation !== false) {
    const reputation = ReputationManager.getReputation(species.id);
    if (reputation <= -50) { // ENEMY level
      return true; // Always attack enemies
    }
    if (reputation >= 50) { // ALLIED level
      return false; // Never attack allies
    }
  }
  
  // Hostile aliens always attack
  if (hostility >= 10) return true;
  
  // Aggressive aliens have high chance
  if (hostility >= 7) {
    return Math.random() < 0.7; // 70% chance
  }
  
  // Neutral aliens rarely attack unprovoked
  if (hostility >= 4) {
    return Math.random() < 0.2; // 20% chance
  }
  
  // Cautious and peaceful never attack first
  return false;
};

/**
 * Determine if alien will flee from player
 * @param {Object} species - Alien species object
 * @param {Object} context - Encounter context
 * @returns {boolean} True if alien flees
 */
export const willFlee = (species, context = {}) => {
  const hostility = getHostilityLevel(species);
  
  // Peaceful and cautious aliens flee easily
  if (hostility <= 3) {
    return Math.random() < 0.6; // 60% chance
  }
  
  // Neutral aliens sometimes flee
  if (hostility <= 6) {
    return Math.random() < 0.3; // 30% chance
  }
  
  // Aggressive and hostile rarely flee
  return Math.random() < 0.1; // 10% chance
};

// ============================================================================
// ENCOUNTER GENERATION
// ============================================================================

/**
 * Generate alien encounter with behavior
 * @param {string} alienId - Alien species ID
 * @param {Object} context - Encounter context
 * @returns {Object} Complete encounter object
 */
export const generateEncounter = (alienId, context = {}) => {
  const species = getAlienSpecies(alienId);
  if (!species) return null;

  const hostility = getHostilityLevel(species);
  const category = getHostilityCategory(hostility);
  const willAttack = willAttackOnSight(species, context);
  const dialogue = getRandomDialogue(species);

  return {
    alienId: species.id,
    alienName: species.name,
    species: species,
    hostility: {
      level: hostility,
      category: category,
      color: getHostilityColor(hostility),
      willAttack: willAttack,
      canNegotiate: hostility < 10
    },
    dialogue: dialogue,
    appearance: species.appearance,
    encounterType: context.encounterType || 'exploration',
    timestamp: Date.now()
  };
};

/**
 * Get random dialogue from species
 * @param {Object} species - Alien species object
 * @returns {string} Random dialogue line
 */
export const getRandomDialogue = (species) => {
  if (!species || !species.dialogue || species.dialogue.length === 0) {
    return 'Hello, traveler.';
  }
  return species.dialogue[Math.floor(Math.random() * species.dialogue.length)];
};

// ============================================================================
// INTERACTION OUTCOMES
// ============================================================================

/**
 * Process friendly interaction attempt
 * @param {Object} encounter - Encounter object from generateEncounter
 * @param {Object} playerState - Player state (reputation, diplomacy skill, etc.)
 * @returns {Object} Interaction result
 */
export const processFriendlyInteraction = (encounter, playerState = {}) => {
  const { species, hostility } = encounter;
  
  // Hostile aliens reject friendly attempts
  if (hostility.level >= 10) {
    return {
      success: false,
      outcome: 'REJECTED',
      message: `The ${species.name} ignore your peaceful gestures and prepare to attack!`,
      credits: 0,
      damage: 0,
      color: '#ff4444',
      reputationChange: 0
    };
  }
  
  // Calculate success chance based on hostility
  const baseChance = 1.0 - (hostility.level / 10);
  const successChance = Math.max(0.2, baseChance); // Minimum 20% chance
  
  const success = Math.random() < successChance;
  
  if (success) {
    // Successful friendly contact
    const bonus = species.tradeable ? (species.tradeValue || 200) : 150;
    
    // Reputation gain: +5 to +20 based on how friendly the interaction was
    const repGain = Math.floor(5 + Math.random() * 15);
    ReputationManager.modifyReputation(species.id, repGain, 'Friendly interaction');
    
    return {
      success: true,
      outcome: 'FRIENDLY_CONTACT',
      message: `The ${species.name} respond positively to your friendly approach. They share valuable information and resources.`,
      credits: bonus,
      damage: 0,
      color: '#00ff88',
      reputationChange: repGain
    };
  } else {
    // Failed attempt - alien is wary (no reputation change)
    return {
      success: false,
      outcome: 'WARY',
      message: `The ${species.name} are suspicious of your intentions and keep their distance.`,
      credits: 0,
      damage: 0,
      color: '#ffaa00',
      reputationChange: 0
    };
  }
};

/**
 * Process aggressive/defensive action
 * @param {Object} encounter - Encounter object
 * @param {Object} playerState - Player state
 * @returns {Object} Combat result
 */
export const processAggressiveAction = (encounter, playerState = {}) => {
  const { species, hostility } = encounter;
  
  // Peaceful aliens flee
  if (hostility.level <= 1) {
    // Attacking peaceful aliens is bad for reputation
    const repLoss = -15;
    ReputationManager.modifyReputation(species.id, repLoss, 'Attacked peaceful species');
    
    return {
      success: true,
      outcome: 'FLED',
      message: `The ${species.name} flee from your aggressive posture.`,
      credits: 0,
      damage: 0,
      color: '#00ffff',
      reputationChange: repLoss
    };
  }
  
  // Combat initiated
  const alienStrength = hostility.level * 10; // 0-100 strength
  const playerStrength = playerState.weaponPower || 50;
  
  const playerWins = playerStrength > alienStrength || Math.random() < 0.6;
  
  if (playerWins) {
    // Reputation change depends on if species was hostile
    let repChange;
    if (species.hostile || hostility.level >= 8) {
      repChange = 5; // Gain rep for defeating hostile species
      ReputationManager.modifyReputation(species.id, repChange, 'Defeated hostile species');
    } else {
      repChange = -10; // Lose rep for attacking non-hostile species
      ReputationManager.modifyReputation(species.id, repChange, 'Attacked non-hostile species');
    }
    
    return {
      success: true,
      outcome: 'COMBAT_VICTORY',
      message: `You successfully defend against the ${species.name}!`,
      credits: 100,
      damage: Math.floor(alienStrength * 0.3), // Take some damage
      color: '#00ff88',
      reputationChange: repChange
    };
  } else {
    // Lost combat - small reputation loss
    const repLoss = -5;
    ReputationManager.modifyReputation(species.id, repLoss, 'Lost combat');
    
    return {
      success: false,
      outcome: 'COMBAT_DEFEAT',
      message: `The ${species.name} overpower your defenses!`,
      credits: -50,
      damage: Math.floor(alienStrength * 0.6), // Take heavy damage
      color: '#ff4444',
      reputationChange: repLoss
    };
  }
};

/**
 * Process evasion/flee attempt
 * @param {Object} encounter - Encounter object
 * @param {Object} playerState - Player state
 * @returns {Object} Evasion result
 */
export const processEvasion = (encounter, playerState = {}) => {
  const { species, hostility } = encounter;
  
  // Calculate escape chance (easier against less hostile aliens)
  const escapeChance = 0.9 - (hostility.level * 0.05); // 90% to 40%
  const success = Math.random() < escapeChance;
  
  if (success) {
    // Small reputation loss for fleeing (shows weakness/fear)
    const repLoss = -5;
    ReputationManager.modifyReputation(species.id, repLoss, 'Fled from encounter');
    
    return {
      success: true,
      outcome: 'ESCAPED',
      message: `You successfully evade the ${species.name} and continue exploring.`,
      credits: 0,
      damage: 0,
      color: '#00ffff',
      reputationChange: repLoss
    };
  } else {
    // Failed escape - forced combat, no reputation change
    const damage = Math.floor(hostility.level * 5);
    return {
      success: false,
      outcome: 'CAUGHT',
      message: `The ${species.name} catch up to you! You take damage while escaping.`,
      credits: 0,
      damage: damage,
      color: '#ff8800',
      reputationChange: 0
    };
  }
};

/**
 * Process trade attempt
 * @param {Object} encounter - Encounter object
 * @param {Object} playerState - Player state
 * @returns {Object} Trade result
 */
export const processTrade = (encounter, playerState = {}) => {
  const { species, hostility } = encounter;
  
  // Can't trade with hostile aliens
  if (hostility.level >= 8) {
    return {
      success: false,
      outcome: 'TRADE_REJECTED',
      message: `The ${species.name} are too hostile to trade!`,
      credits: 0,
      damage: 0,
      color: '#ff4444',
      reputationChange: 0
    };
  }
  
  // Can't trade with non-tradeable species
  if (!species.tradeable) {
    return {
      success: false,
      outcome: 'NO_TRADE',
      message: `The ${species.name} have nothing to trade.`,
      credits: 0,
      damage: 0,
      color: '#ffaa00',
      reputationChange: 0
    };
  }
  
  // Successful trade - good reputation gain
  const tradeValue = species.tradeValue || 200;
  
  // Better reputation = better prices
  const reputation = ReputationManager.getReputation(species.id);
  let priceMultiplier = 1.0;
  if (reputation >= 50) {
    priceMultiplier = 1.3; // ALLIED: 30% bonus
  } else if (reputation >= 10) {
    priceMultiplier = 1.15; // FRIENDLY: 15% bonus
  } else if (reputation <= -10) {
    priceMultiplier = 0.85; // UNFRIENDLY: 15% penalty
  }
  
  const profit = Math.floor(tradeValue * (0.8 + Math.random() * 0.4) * priceMultiplier);
  
  // Reputation gain from trading
  const repGain = 10;
  ReputationManager.modifyReputation(species.id, repGain, 'Successful trade');
  
  return {
    success: true,
    outcome: 'TRADE_SUCCESS',
    message: `You successfully trade with the ${species.name}. They offer exotic goods and information.`,
    credits: profit,
    damage: 0,
    color: '#00ff88',
    reputationChange: repGain
  };
};

// ============================================================================
// UNIVERSE-SPECIFIC QUERIES
// ============================================================================

/**
 * Get aliens for a specific planet
 * @param {number} planetId - Planet ID
 * @param {Object} universeAliens - Universe alien config
 * @returns {Array} Array of alien species objects
 */
export const getAliensForPlanet = (planetId, universeAliens) => {
  if (!universeAliens || !universeAliens.planets) {
    return [];
  }
  
  const alienIds = universeAliens.planets[planetId] || [];
  return alienIds.map(id => getAlienSpecies(id)).filter(Boolean);
};

/**
 * Get random alien from planet
 * @param {number} planetId - Planet ID
 * @param {Object} universeAliens - Universe alien config
 * @returns {Object|null} Alien species object or null
 */
export const getRandomAlienFromPlanet = (planetId, universeAliens) => {
  const aliens = getAliensForPlanet(planetId, universeAliens);
  if (aliens.length === 0) return null;
  return aliens[Math.floor(Math.random() * aliens.length)];
};

/**
 * Get random encounter alien from universe
 * @param {Object} universeAliens - Universe alien config
 * @returns {Object|null} Alien species object or null
 */
export const getRandomEncounterAlien = (universeAliens) => {
  if (!universeAliens || !universeAliens.randomEncounters) {
    return null;
  }
  
  const alienIds = universeAliens.randomEncounters;
  const randomId = alienIds[Math.floor(Math.random() * alienIds.length)];
  return getAlienSpecies(randomId);
};

/**
 * Get random trader from universe
 * @param {Object} universeAliens - Universe alien config
 * @returns {Object|null} Alien species object or null
 */
export const getRandomTrader = (universeAliens) => {
  if (!universeAliens || !universeAliens.friendlyTraders) {
    return null;
  }
  
  const alienIds = universeAliens.friendlyTraders;
  const randomId = alienIds[Math.floor(Math.random() * alienIds.length)];
  return getAlienSpecies(randomId);
};

// ============================================================================
// FORMATTING & DISPLAY
// ============================================================================

/**
 * Format alien info for scan results
 * @param {Array} aliens - Array of alien species objects
 * @returns {string} Formatted alien info
 */
export const formatAlienInfoForScan = (aliens) => {
  if (!aliens || aliens.length === 0) {
    return 'No intelligent life detected.';
  }

  const alienNames = aliens.map(species => 
    `${species.appearance.ascii} ${species.name}`
  );

  return `Intelligent life detected: ${alienNames.join(', ')}`;
};

/**
 * Get alien visual representation
 * @param {string} alienId - Alien species ID
 * @returns {Object} Visual representation
 */
export const getAlienVisual = (alienId) => {
  const species = getAlienSpecies(alienId);
  
  if (!species) {
    return {
      ascii: '👽',
      color: '#808080',
      description: 'Unknown alien'
    };
  }

  return {
    ascii: species.appearance.ascii,
    color: species.appearance.color,
    description: species.appearance.description
  };
};

/**
 * Create encounter notification data
 * @param {Object} encounter - Encounter object from generateEncounter
 * @returns {Object} Notification data for EventNotification component
 */
export const createEncounterNotification = (encounter) => {
  const { species, hostility, dialogue } = encounter;
  
  return {
    type: 'ALIEN_ENCOUNTER',
    title: `${species.appearance.ascii} ${species.name.toUpperCase()} DETECTED`,
    message: `${species.description}\n\n"${dialogue}"\n\nHostility: ${hostility.category} (${hostility.level}/10)\nOrigin: ${species.origin}\nTechnology: ${species.traits.technology}`,
    color: hostility.color,
    icon: species.appearance.ascii,
    hostility: hostility
  };
};

// ============================================================================
// STATISTICS & ANALYTICS
// ============================================================================

/**
 * Get all aliens sorted by hostility
 * @returns {Array} Sorted array of alien species
 */
export const getAliensByHostility = () => {
  const allAliens = getAllAlienSpecies();
  return allAliens
    .map(species => ({
      ...species,
      hostilityLevel: getHostilityLevel(species)
    }))
    .sort((a, b) => a.hostilityLevel - b.hostilityLevel);
};

/**
 * Get aliens by hostility category
 * @param {string} category - PEACEFUL, CAUTIOUS, NEUTRAL, AGGRESSIVE, HOSTILE
 * @returns {Array} Filtered array of alien species
 */
export const getAliensByCategory = (category) => {
  const allAliens = getAllAlienSpecies();
  return allAliens.filter(species => {
    const level = getHostilityLevel(species);
    return getHostilityCategory(level) === category;
  });
};

/**
 * Get encounter statistics for an alien species
 * @param {string} alienId - Alien species ID
 * @returns {Object} Statistics object
 */
export const getAlienStats = (alienId) => {
  const species = getAlienSpecies(alienId);
  if (!species) return null;
  
  const hostility = getHostilityLevel(species);
  
  return {
    id: species.id,
    name: species.name,
    hostility: {
      level: hostility,
      category: getHostilityCategory(hostility),
      color: getHostilityColor(hostility)
    },
    tradeable: species.tradeable,
    tradeValue: species.tradeValue || 0,
    attackChance: hostility >= 7 ? 0.7 : hostility >= 4 ? 0.2 : 0,
    fleeChance: hostility <= 3 ? 0.6 : hostility <= 6 ? 0.3 : 0.1,
    negotiable: hostility < 10,
    traits: species.traits
  };
};

// ============================================================================
// EXPORTS
// ============================================================================

const AlienManagerAPI = {
  // Hostility System
  getHostilityLevel,
  getHostilityCategory,
  getHostilityColor,
  willAttackOnSight,
  willFlee,
  
  // Encounter Generation
  generateEncounter,
  getRandomDialogue,
  
  // Interaction Outcomes
  processFriendlyInteraction,
  processAggressiveAction,
  processEvasion,
  processTrade,
  
  // Universe Queries
  getAliensForPlanet,
  getRandomAlienFromPlanet,
  getRandomEncounterAlien,
  getRandomTrader,
  
  // Formatting
  formatAlienInfoForScan,
  getAlienVisual,
  createEncounterNotification,
  
  // Statistics
  getAliensByHostility,
  getAliensByCategory,
  getAlienStats
};

export default AlienManagerAPI;
